import React, { useMemo, useState } from 'react';
import { Autocomplete, InputAdornment, TextField } from '@mui/material';
import { Search } from 'lucide-react';
import { PersonCell } from '../../../components/ui';
import { sbg } from '../../../theme/sbgTokens';
import { FloorplanEmployee } from '../../../types/floorplan';

const MAX_RESULTS = 8;

interface PersonSearchProps {
  people: FloorplanEmployee[];
  /** One disambiguating fact per person: "Desk 14 · Level 2" or "No desk yet". */
  describe: (person: FloorplanEmployee) => string;
  /** Called with the chosen person; the field clears itself afterwards. */
  onSelect: (person: FloorplanEmployee) => void;
}

const matches = (person: FloorplanEmployee, query: string) =>
  person.name.toLowerCase().includes(query)
  || person.email.toLowerCase().includes(query)
  || (person.teamName || '').toLowerCase().includes(query)
  || (person.department?.name || '').toLowerCase().includes(query);

/**
 * Find a person: the page jumps to their desk, or opens their record when they
 * don't have one yet. A shortcut over existing actions; it never changes an
 * assignment.
 */
const PersonSearch: React.FC<PersonSearchProps> = ({ people, describe, onSelect }) => {
  const [input, setInput] = useState('');
  const sorted = useMemo(() => [...people].sort((left, right) => left.name.localeCompare(right.name)), [people]);

  return (
    <Autocomplete<FloorplanEmployee, false, false, false>
      options={sorted}
      value={null}
      inputValue={input}
      onInputChange={(_event, value, reason) => setInput(reason === 'reset' ? '' : value)}
      onChange={(_event, person) => {
        if (person) {
          onSelect(person);
          setInput('');
        }
      }}
      getOptionLabel={(person) => person.name}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      filterOptions={(options, state) => {
        const query = state.inputValue.trim().toLowerCase();
        return (query ? options.filter((person) => matches(person, query)) : options).slice(0, MAX_RESULTS);
      }}
      blurOnSelect
      clearOnBlur
      forcePopupIcon={false}
      noOptionsText="No one matches that search"
      renderOption={(props, person) => {
        const { key, ...optionProps } = props as React.HTMLAttributes<HTMLLIElement> & { key: React.Key };
        return (
          <li key={key} {...optionProps}>
            <PersonCell name={person.name} secondary={describe(person)} size={24} />
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Find a person"
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start" sx={{ color: sbg.text.secondary }}>
                <Search size={16} aria-hidden="true" />
              </InputAdornment>
            ),
          }}
          inputProps={{ ...params.inputProps, 'aria-label': 'Find a person on the floor plan' }}
        />
      )}
      sx={{
        width: { xs: '100%', sm: 260 },
        '& .MuiOutlinedInput-root': { height: 34, py: 0, borderRadius: sbg.radius.pill, paddingLeft: '12px' },
      }}
    />
  );
};

export default PersonSearch;
